/**
 * Enquiry Operations API
 * API functions for managing enquiry actions (take, update status, bulk operations)
 */

import { Enquiry, EnquiryStatus } from "../../types/enquiries";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";

type BookingStatus =
  | "not_started"
  | "new"
  | "in_progress"
  | "complete"
  | "rejected"
  | "cancel_request"
  | "lost"
  | "deleted";

interface BookingApiResponse {
  payload: {
    booking: {
      _id: string;
      booking_id?: string;
      status: BookingStatus;
      assigned_to?: string;
      assigned_to_name?: string;
      labels?: string[];
      customer_labels?: string[];
    };
  };
}

const postBookingUpdate = async (
  enquiryId: string,
  body: Record<string, unknown>,
  errorLabel: string,
  failureMessage: string
): Promise<BookingApiResponse["payload"]["booking"]> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${enquiryId}`;

  let response;

  try {
    response = await fetch(bookingUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    });
  } catch (error) {
    errorHandler(error, errorLabel);
    throw error;
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, errorLabel);
    throw new Error(failureMessage);
  }

  const data: BookingApiResponse = await response.json();
  return data.payload.booking;
};

const mapBookingStatusToEnquiryStatus = (
  status: BookingStatus | undefined
): EnquiryStatus => {
  if (status === "new" || status === "not_started") {
    return "new";
  }
  if (status === "in_progress") {
    return "in_progress";
  }
  if (status === "complete") {
    return "complete";
  }
  if (status === "cancel_request" || status === "rejected") {
    return "cancelled";
  }
  if (status === "lost") {
    return "lost";
  }
  if (status === "deleted") {
    return "deleted";
  }

  return "new";
};

/**
 * Takes an enquiry and assigns it to a user
 * This updates the booking by setting assigned_to to the current user's ID
 */
export const takeEnquiryRequest = async (
  enquiryId: string,
  userId: string
): Promise<Enquiry> => {
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { assigned_to: userId },
    "Error taking enquiry",
    "Failed to take enquiry"
  );

  return {
    _id: updatedBooking._id,
    reference: updatedBooking.booking_id ?? updatedBooking._id,
    status: mapBookingStatusToEnquiryStatus(updatedBooking.status),
    assigned_to: updatedBooking.assigned_to,
    assigned_to_name: updatedBooking.assigned_to_name
  };
};

/**
 * Maps an EnquiryStatus to the corresponding BookingStatus used by the Core API.
 * Note: 'cancelled' in the client maps to 'rejected' in the API.
 */
const mapEnquiryStatusToBookingStatus = (
  status: EnquiryStatus
): BookingStatus => {
  if (status === "cancelled") {
    return "rejected";
  }
  if (
    status === "new" ||
    status === "in_progress" ||
    status === "complete" ||
    status === "lost" ||
    status === "deleted"
  ) {
    return status;
  }

  const _exhaustive: never = status;
  throw new Error(`Unexpected enquiry status: ${_exhaustive}`);
};

/**
 * Updates the status of an enquiry via the Core API.
 * Routes to the appropriate API call based on the target status:
 * - 'deleted' uses a DELETE request (inspired by collins-ui bookingDetails.js sendDeleteRequest)
 * - 'complete' and 'cancelled' use dedicated request functions
 * - Other statuses (new, in_progress, lost) POST the status directly
 */
export const updateEnquiryStatusRequest = async (
  enquiryId: string,
  status: EnquiryStatus
): Promise<Enquiry> => {
  if (status === "deleted") {
    return deleteEnquiryRequest(enquiryId);
  }
  if (status === "complete") {
    return confirmEnquiryRequest(enquiryId);
  }
  if (status === "cancelled") {
    return rejectEnquiryRequest(enquiryId);
  }

  return setEnquiryStatusRequest(enquiryId, status);
};

/**
 * Sets an enquiry status directly via the Core API.
 * Used for statuses that simply update the booking status field
 * (new, in_progress, lost). Follows the same pattern as
 * collins-ui bookingDetails.js reopenBooking / completeBooking.
 */
const setEnquiryStatusRequest = async (
  enquiryId: string,
  status: EnquiryStatus
): Promise<Enquiry> => {
  const bookingStatus = mapEnquiryStatusToBookingStatus(status);
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { status: bookingStatus },
    `Error updating enquiry status to ${status}`,
    `Failed to update enquiry status to ${status}`
  );

  return {
    _id: updatedBooking._id,
    reference: updatedBooking.booking_id ?? updatedBooking._id,
    status: mapBookingStatusToEnquiryStatus(updatedBooking.status),
    assigned_to: updatedBooking.assigned_to,
    assigned_to_name: updatedBooking.assigned_to_name,
    labels: updatedBooking.labels
  };
};

/**
 * Deletes an enquiry via the Core API using a DELETE request.
 * Inspired by collins-ui bookingDetails.js sendDeleteRequest / BookingsProvider.delete.
 * The Core API soft-deletes the booking, so it can be restored later.
 */
export const deleteEnquiryRequest = async (
  enquiryId: string
): Promise<Enquiry> => {
  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);
  const bookingUrl = `${environmentVars?.CORE_API_BASE_URL}/bookings/${enquiryId}`;

  let reference = enquiryId;

  try {
    const referenceResponse = await fetch(bookingUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (referenceResponse.ok) {
      const referenceData: BookingApiResponse = await referenceResponse.json();
      const existingBooking = referenceData.payload.booking;
      reference = existingBooking.booking_id ?? existingBooking._id;
    }
  } catch {
    // Ignore reference fetch errors
  }

  let response;

  try {
    response = await fetch(bookingUrl, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        reason: "other"
      })
    });
  } catch (error) {
    errorHandler(error, "Error deleting enquiry");
    throw error;
  }

  if (!response?.ok) {
    apiFetchNotOkHandler(response, "Error deleting enquiry");
    throw new Error("Failed to delete enquiry");
  }

  return {
    _id: enquiryId,
    reference,
    status: "deleted"
  };
};

/**
 * Rejects an enquiry by setting its status to rejected
 * This updates the booking status via the Core API
 * Inspired by collins-ui bookingDetails.js rejectBooking function
 */
export const rejectEnquiryRequest = async (
  enquiryId: string
): Promise<Enquiry> => {
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { status: "rejected" },
    "Error rejecting enquiry",
    "Failed to reject enquiry"
  );

  return {
    _id: updatedBooking._id,
    reference: updatedBooking.booking_id ?? updatedBooking._id,
    status: mapBookingStatusToEnquiryStatus(updatedBooking.status),
    assigned_to: updatedBooking.assigned_to,
    assigned_to_name: updatedBooking.assigned_to_name,
    labels: updatedBooking.labels
  };
};

/**
 * Confirms an enquiry by setting its status to complete
 * This updates the booking status via the Core API
 */
export const confirmEnquiryRequest = async (
  enquiryId: string
): Promise<Enquiry> => {
  const updatedBooking = await postBookingUpdate(
    enquiryId,
    { status: "complete" },
    "Error confirming enquiry",
    "Failed to confirm enquiry"
  );

  return {
    _id: updatedBooking._id,
    reference: updatedBooking.booking_id ?? updatedBooking._id,
    status: mapBookingStatusToEnquiryStatus(updatedBooking.status),
    assigned_to: updatedBooking.assigned_to,
    assigned_to_name: updatedBooking.assigned_to_name,
    labels: updatedBooking.labels
  };
};

/**
 * Bulk assign enquiries to a user
 * TODO: Implement real API call when endpoint is available
 */
export const bulkAssignTo = async (
  enquiryIds: string[],
  _userId: string // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<{ success: boolean; count: number }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk set status for enquiries
 * TODO: Implement real API call when endpoint is available
 */
export const bulkSetStatus = async (
  enquiryIds: string[],
  _status: EnquiryStatus // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<{ success: boolean; count: number }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk add labels to enquiries
 * TODO: Implement real API call when endpoint is available
 */
export const bulkAddLabels = async (
  enquiryIds: string[],
  _labels: string[] // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<{ success: boolean; count: number }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk set follow-up date for enquiries
 * TODO: Implement real API call when endpoint is available
 */
export const bulkSetFollowUp = async (
  enquiryIds: string[],
  _followUpDate: string // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<{ success: boolean; count: number }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk reject enquiries
 * TODO: Implement real API call when endpoint is available
 */
export const bulkReject = async (
  enquiryIds: string[]
): Promise<{ success: boolean; count: number }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, count: enquiryIds.length };
};

/**
 * Bulk delete enquiries
 * TODO: Implement real API call when endpoint is available
 */
export const bulkDelete = async (
  enquiryIds: string[]
): Promise<{ success: boolean; count: number }> => {
  await new Promise((resolve) => setTimeout(resolve, 500));
  return { success: true, count: enquiryIds.length };
};
